import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material'
import PropTypes from 'prop-types'

const classes = {
    formControl_medium: {
        width: '180px',
    },
}

/**
 * Material consumption mode selector for quote items.
 * Allows users to select how material consumption is calculated.
 * Memoized to prevent unnecessary re-renders when parent state changes.
 *
 * @param {Object} props - Component props
 * @param {string} props.value - The currently selected consumption mode value
 * @param {Array<{value: string, label: string}>} props.options - Available consumption mode options
 * @param {boolean} [props.disabled=false] - Whether the selector is disabled
 * @param {Function} props.onChange - Callback when selection changes
 * @returns {React.ReactElement} A row with label and consumption mode dropdown
 */
const ConsumptionModeSelector = ({ disabled = false, onChange, options, value }) => {
    const { t } = useTranslation()

    return (
        <Box
            sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
            }}
        >
            <Typography data-testid="nqp-quote-item-consumption-mode-label">{t('Consumption mode')}</Typography>
            <FormControl
                color="primary"
                margin="none"
                size="small"
                sx={classes.formControl_medium}
                variant="outlined"
            >
                <Select
                    data-testid="nqp-quote-item-consumption-mode"
                    disabled={disabled}
                    id="select-itemConsumptionMode"
                    labelId="select-itemConsumptionMode-label"
                    SelectDisplayProps={{ style: { textAlign: 'left' } }}
                    value={value}
                    onChange={(e) => onChange(e)}
                >
                    {options.map((mode) => (
                        <MenuItem
                            key={mode.value}
                            value={mode.value}
                        >
                            {mode.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    )
}

ConsumptionModeSelector.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        })
    ).isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
}

export default memo(ConsumptionModeSelector)
